import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  BadgeCheck,
  FileText,
  Leaf,
  Home,
  Droplets,
  Sprout,
  Sparkles,
  Wrench,
  CheckCircle2,
  MapPin,
  Star,
  ArrowUpRight,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FAQ } from '../components/FAQ';
import { ServiceAreaMap } from '../components/ServiceAreaMap';
import { SITE, AREAS, REVIEWS } from '../data/site';

const setMeta = (name, content, attr = 'name') => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const LOCAL_PHOTOS = [
  { url: '/gallery/ba-moss-gutter.jpg', caption: 'Before &amp; after — a UPVC gutter, fascia and soffit replacement' },
  { url: '/gallery/20250610_101401.jpg', caption: 'A Leamington Spa roof heavily colonised by moss before treatment' },
  { url: '/gallery/concrete-vs-upvc.jpg', caption: 'A gutter after one of our hand clear outs' },
  { url: '/gallery/new-gutter-clean.jpg', caption: 'A hand cleaned gutter on a Warwickshire property' },
  { url: '/gallery/ba-conservatory-clean.jpg', caption: 'Conservatory roof deep-cleaned — grime and moss lifted, light restored' },
  { url: '/gallery/20260407_172254.jpg', caption: 'A Black Gutter and Fascia with white soffit replacement in Radford Semele, Leamington Spa' },
];

const CTA = ({ testid = 'landing-cta' }) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <Link
      to="/#contact"
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 bg-[#E8B842] hover:bg-[#F4C534] text-black font-display font-bold uppercase tracking-wider px-7 py-3.5 transition-colors"
    >
      Get a Free Quote
      <ArrowRight className="w-4 h-4" />
    </Link>
    <a
      href={SITE.phoneTel}
      data-testid={`${testid}-call`}
      className="inline-flex items-center justify-center gap-2 border-2 border-[#E8B842] text-[#E8B842] hover:bg-[#E8B842] hover:text-black font-display font-bold uppercase tracking-wider px-7 py-3.5 transition-colors"
    >
      <Phone className="w-4 h-4" />
      {SITE.phoneDisplay}
    </a>
  </div>
);

export default function GutterCleaningLeamingtonSpaPage() {
  useEffect(() => {
    document.title =
      'Gutter Cleaning Leamington Spa | Local Gutter Cleaners | Leamington Gutters';
    setMeta(
      'description',
      'Trusted gutter cleaning in Leamington Spa, Warwick, Kenilworth and surrounding villages. Independent local specialists — moss removal, hand cleaning, leaking gutter repair. Fully insured, guaranteed work, free quotes. Call Luke on 07788 287 003.',
    );
    setMeta('keywords', 'gutter cleaning Leamington Spa, gutter cleaners Leamington Spa, gutter clearing Warwickshire, gutter maintenance Warwick, gutter moss removal Kenilworth, hand gutter cleaning Whitnash, gutter cleaning cost Leamington Spa');
    setCanonical('https://leamingtongutters.uk/gutter-cleaning-leamington-spa');
    setMeta('og:title', 'Gutter Cleaning Leamington Spa | Leamington Gutters', 'property');
    setMeta('og:url', 'https://leamingtongutters.uk/gutter-cleaning-leamington-spa', 'property');
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 bg-[#050505] overflow-hidden">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <img
              src="/gallery/black-gutter-garden.jpg"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/95 to-[#050505]/70" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 border border-[#E8B842]/40 px-4 py-2 mb-6">
              <MapPin className="w-3.5 h-3.5 text-[#E8B842]" />
              <span className="font-display uppercase tracking-[0.3em] text-xs text-[#E8B842]">
                Leamington Spa &middot; Warwickshire
              </span>
            </div>
            <h1 className="font-display uppercase font-extrabold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
              Gutter Cleaning in <span className="text-[#E8B842]">Leamington Spa</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#C7C7C7] max-w-3xl leading-relaxed">
              Reliable, hand-clean gutter services from Leamington Gutters &mdash; your
              independent local specialist. Moss, silt, leaves and seedlings cleared safely
              from your gutters, downpipes and hoppers, so rainwater flows where it should.
            </p>

            <div className="mt-8"><CTA testid="landing-hero-cta" /></div>

            {/* Trust strip */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
              {[
                { Icon: ShieldCheck, label: 'Fully Insured' },
                { Icon: BadgeCheck, label: 'Guaranteed Work' },
                { Icon: FileText, label: 'Free Quotations' },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-3 border-l-2 border-[#E8B842] pl-4 py-2">
                  <Icon className="w-5 h-5 text-[#E8B842]" />
                  <span className="font-display uppercase tracking-wider text-sm text-white">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Intro / Why it matters */}
        <section className="py-20 sm:py-24 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose-invert">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Why clean gutters matter
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              A local gutter clean is the single cheapest thing you can do to protect your home
            </h2>
            <div className="mt-6 space-y-5 text-[#C7C7C7] text-base sm:text-lg leading-relaxed">
              <p>
                In a Warwickshire town like <strong>Royal Leamington Spa</strong>, gutters take
                a real beating. Regency terraces around The Parade, red-brick semis in Lillington
                and Whitnash, period cottages in Old Milverton, and modern estates in Heathcote
                all share the same problem: a mix of mature trees, damp winters and long,
                drizzly autumns that pack gutters full of leaves, silt, moss and seed debris.
              </p>
              <p>
                When a gutter blocks, rainwater has nowhere to go. Instead of running to the
                downpipe, it spills over the front of the gutter, soaks the fascia, drips down
                the brickwork, and pools around the foundations. Over a few winters that
                overflow rots timber fascia and soffit boards, stains and erodes brick and
                mortar, causes damp patches on internal walls, and &mdash; in the worst cases
                &mdash; lets water track down and into the roof space itself.
              </p>
              <p>
                A quick, thorough hand-clean of your gutters is by far the cheapest and easiest
                way to stop all of that. It typically costs a fraction of a single fascia
                repair, and if you do it regularly you may never need one.
              </p>
            </div>
          </div>
        </section>

        {/* Photo grid */}
        <section className="py-8 sm:py-12 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {LOCAL_PHOTOS.map((p, i) => (
                <figure key={p.url} className="relative overflow-hidden bg-[#121212] border border-[#E8B842]/10 aspect-[4/5] group">
                  <img
                    src={p.url}
                    alt={p.caption.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&rsquo;/g, '\u2019')}
                    loading={i < 2 ? 'eager' : 'lazy'}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-transparent pointer-events-none" />
                  <figcaption
                    className="absolute bottom-0 left-0 right-0 p-4 font-display uppercase tracking-wider text-xs sm:text-sm text-[#E8B842] font-bold drop-shadow"
                    dangerouslySetInnerHTML={{ __html: p.caption }}
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* What we do */}
        <section className="py-20 sm:py-24 bg-[#0A0A0A] border-y border-[#E8B842]/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / What our gutter clean includes
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              A thorough, methodical clean &mdash; not a rushed job
            </h2>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              {[
                { Icon: Sprout, t: 'Full clear of moss, silt & debris', b: 'We hand-clear every gutter run from end to end, lifting out moss balls, seedlings, twigs and packed silt. Nothing is pushed into the downpipe to become tomorrow&rsquo;s problem.' },
                { Icon: Droplets, t: 'Downpipes and hoppers checked', b: 'Downpipes and hopper heads are cleared and, where required, run through with water to make sure everything flows freely down to the drain.' },
                { Icon: Sparkles, t: 'Hand cleaning of the Gutter, Fascia & Soffit', b: 'We hand clean the gutter, fascia and soffit to remove black streaks, grime and weathering so your roofline looks fresh from the ground.' },
                { Icon: Home, t: 'Tidy site and photo report', b: 'All debris is bagged and taken away. Luke will happily send you before-and-after photos so you can see the difference for yourself.' },
              ].map(({ Icon, t, b }) => (
                <article key={t} className="bg-[#0E0E0E] border border-[#E8B842]/15 p-6 hover:border-[#E8B842]/50 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#E8B842]/10 border border-[#E8B842]/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#E8B842]" />
                    </div>
                    <h3 className="font-display uppercase tracking-wider text-white text-lg font-bold">{t}</h3>
                  </div>
                  <p className="text-[#C7C7C7] text-sm sm:text-base leading-relaxed" dangerouslySetInnerHTML={{__html: b}} />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* When to book */}
        <section className="py-20 sm:py-24 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / When to book
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              Late autumn and early spring are best
            </h2>
            <div className="mt-6 space-y-5 text-[#C7C7C7] text-base sm:text-lg leading-relaxed">
              <p>
                Most homeowners in Leamington Spa book us either in <strong>late autumn</strong>{' '}
                &mdash; once the leaves have finished falling from the mature limes, sycamores
                and beeches around the town &mdash; or in <strong>early spring</strong>, before
                the summer growth starts and before spring rain arrives in earnest.
              </p>
              <p>
                If your property is close to trees, a twice-yearly clean is usually the best way
                to keep on top of it. If you&rsquo;re further out towards the villages such as
                Cubbington, Hatton, Barford or Harbury, and you&rsquo;re surrounded by farmland
                or hedgerows, a single autumn clean is often plenty.
              </p>
              <p>
                We also come out for one-off emergency clears &amp; repairs &mdash; if
                you&rsquo;ve got water pouring over the gutter during a downpour, don&rsquo;t
                wait for the season to come round; give us a ring and we&rsquo;ll get to you
                as soon as possible.
              </p>
            </div>
          </div>
        </section>

        {/* Signs you need a clean */}
        <section className="py-20 sm:py-24 bg-[#0A0A0A] border-y border-[#E8B842]/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Signs your gutters need cleaning
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              Look for these tell-tale signs
            </h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                'Water overflowing at the front or back of the gutter during rain',
                'Plants, weeds or moss growing out of the top of the gutter',
                'Black streaks or drip marks running down the fascia or brickwork',
                'Sagging or pulled-away gutter sections',
                'Damp patches on interior walls near the roofline',
                'Pooling water beside your foundations after a downpour',
                'Birds nesting or wasps in your gutters',
                'Peeling paint on nearby soffit or fascia boards',
              ].map((sign) => (
                <li key={sign} className="flex items-start gap-3 bg-[#0E0E0E] border border-[#E8B842]/15 p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#E8B842] shrink-0 mt-0.5" />
                  <span className="text-[#E5E5E5] text-sm sm:text-base">{sign}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Why choose us */}
        <section className="py-20 sm:py-24 bg-[#050505]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
                  / Why Leamington homeowners choose us
                </span>
                <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
                  Local, independent and genuinely accountable
                </h2>
                <div className="mt-6 space-y-4 text-[#C7C7C7] text-base sm:text-lg leading-relaxed">
                  <p>
                    Leamington Gutters is an independent, local business based in Leamington
                    Spa. When you ring, you speak to the person doing the work &mdash;
                    there&rsquo;s no call centre, no lead-passing, no middlemen.
                  </p>
                  <p>
                    Every job is <strong className="text-white">fully insured</strong>,
                    <strong className="text-white"> guaranteed</strong>, and completed to the
                    highest standard. We quote fairly, turn up when we say we will, and
                    we&rsquo;ll always be honest with you.
                  </p>
                </div>
              </div>
              <div className="bg-[#0E0E0E] border border-[#E8B842]/20 p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {Array.from({length:5}).map((_,i)=>(<Star key={i} className="w-4 h-4 fill-[#E8B842] text-[#E8B842]" />))}
                  </div>
                  <span className="font-display uppercase tracking-widest text-xs text-white">
                    5.0 &middot; Google Reviews
                  </span>
                </div>
                <blockquote className="mt-5 text-[#E5E5E5] text-base sm:text-lg leading-relaxed">
                  &ldquo;{REVIEWS[0].text.slice(0, 260)}&hellip;&rdquo;
                </blockquote>
                <div className="mt-4 font-display uppercase tracking-wider text-sm text-[#E8B842] font-bold">
                  {REVIEWS[0].name}
                </div>
                <Link
                  to="/#reviews"
                  className="mt-6 inline-flex items-center gap-2 text-[#E8B842] hover:text-[#F4C534] font-display uppercase tracking-wider text-sm"
                >
                  Read more reviews <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 sm:py-24 bg-[#0A0A0A] border-y border-[#E8B842]/15">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Gutter cleaning cost in Leamington Spa
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              Honest, transparent pricing
            </h2>
            <div className="mt-6 space-y-5 text-[#C7C7C7] text-base sm:text-lg leading-relaxed">
              <p>
                Gutter cleaning in Leamington Spa typically starts from around{' '}
                <strong className="text-white">&pound;45</strong> for a small terrace and rises
                with the size and height of the property. Heavy moss build-up, extras like
                conservatories or garages, and awkward access can add to the cost, but we will
                always agree the price with you up front &mdash; no surprises.
              </p>
              <p>
                To get a quote, just call, text or WhatsApp us on{' '}
                <a href={SITE.phoneTel} className="text-[#E8B842] hover:underline">
                  07788 287 003
                </a>{' '}
                with details of the work needed and a photo or two of the property.
                We&rsquo;ll come back to you as soon as possible with a no obligation
                quotation.
              </p>
            </div>
            <div className="mt-8"><CTA testid="landing-pricing-cta" /></div>
          </div>
        </section>

        {/* Areas served */}
        <section className="py-20 sm:py-24 bg-[#050505]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Areas we cover
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4 leading-tight">
              Gutter cleaning across Warwickshire
            </h2>
            <p className="mt-4 text-[#A3A3A3] max-w-2xl">
              Based right in the middle of Leamington Spa, we cover the town centre and every
              surrounding village. Some of the areas we regularly clean gutters in:
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {AREAS.map((a) => (
                <div key={a} className="border border-[#E8B842]/15 bg-[#0E0E0E] px-4 py-3 hover:border-[#E8B842] transition-colors flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#E8B842]" />
                  <span className="font-display uppercase tracking-wider text-sm text-white">{a}</span>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <ServiceAreaMap />
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-16 sm:py-20 bg-[#0A0A0A] border-y border-[#E8B842]/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Related services
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-3xl sm:text-4xl mt-4">
              We also help with&hellip;
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { Icon: Wrench, t: 'Leaking Gutter Repair', b: 'We find and fix leaks at joints, brackets and downpipes.' },
                { Icon: Home, t: 'UPVC Fascia & Soffit Replacement', b: 'Full supply and fit in a range of colours and profiles.' },
                { Icon: Leaf, t: 'Roof Moss Removal', b: 'Safe scraping, brushing and soft-wash treatment.' },
              ].map(({ Icon, t, b }) => (
                <Link to="/#services" key={t} className="group bg-[#0E0E0E] border border-[#E8B842]/15 hover:border-[#E8B842] p-6 transition-colors">
                  <div className="w-11 h-11 bg-[#E8B842]/10 border border-[#E8B842]/30 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#E8B842]" />
                  </div>
                  <h3 className="font-display uppercase tracking-wider text-white text-lg font-bold">{t}</h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed mt-2">{b}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#E8B842] text-sm font-display uppercase tracking-wider">
                    Learn more <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Inline FAQ */}
        <FAQ variant="preview" />

        {/* Final CTA */}
        <section className="py-20 sm:py-24 bg-[#E8B842] text-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display uppercase font-extrabold text-4xl sm:text-5xl leading-tight">
              Ready to book your gutter clean?
            </h2>
            <p className="mt-4 text-base sm:text-lg max-w-2xl mx-auto">
              Send us a message or give us a call &mdash; free, no-obligation quotes across
              Leamington Spa, Warwick, Kenilworth and the surrounding villages.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={SITE.phoneTel}
                data-testid="landing-final-call"
                className="inline-flex items-center justify-center gap-2 bg-black text-[#E8B842] hover:bg-[#1A1A1A] font-display font-bold uppercase tracking-wider px-8 py-4 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call {SITE.contactName} &mdash; {SITE.phoneDisplay}
              </a>
              <Link
                to="/#contact"
                data-testid="landing-final-quote"
                className="inline-flex items-center justify-center gap-2 border-2 border-black text-black hover:bg-black hover:text-[#E8B842] font-display font-bold uppercase tracking-wider px-8 py-4 transition-colors"
              >
                Get a Free Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
